<script setup>
import { ref, computed, onBeforeUnmount } from "vue";

/* ============================================================
   State
============================================================ */
const cpKey = ref("");
const cpBase = ref("");

const testing = ref(false);
const running = ref(false);

const status = ref("");
const error = ref("");
const testResult = ref(null);

const progress = ref([]);
let eventSource = null;

/* ============================================================
   Derived state
============================================================ */
const canStartImport = computed(() =>
  !testing.value &&
  !running.value &&
  cpKey.value.trim() &&
  cpBase.value.trim()
);

/* ============================================================
   Helpers
============================================================ */
function apiHeaders() {
  return {
    "Content-Type": "application/json",
    "x-api-key": localStorage.getItem("api_key"),
  };
}

function resetMessages() {
  error.value = "";
  status.value = "";
  progress.value = [];
}

/* ============================================================
   Test CP connection
============================================================ */
async function testConnection() {
  if (testing.value) return;

  resetMessages();
  testing.value = true;

  try {

    console.log('[debug - cpBase]', cpBase.value.trim());

    const res = await fetch("/api/import/cp/test", {
      method: "POST",
      headers: apiHeaders(),
      body: JSON.stringify({
        cpKey: cpKey.value.trim(),
        cpBase: cpBase.value.trim(),
        purpose: 'test'
      }),
    });

    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Test failed");

    testResult.value = json;
    status.value = `Connection OK — ${json.count} groups found`;
  } catch (err) {
    error.value = err.message || "Connection test failed";
  } finally {
    testing.value = false;
  }
}

/* ============================================================
   Import progress (SSE)
============================================================ */
function startProgressListener() {
  progress.value = [];

  const apiKey = localStorage.getItem("api_key");
  if (!apiKey) {
    error.value = "Missing API key";
    return;
  }

  // 🔑 key via query param (ONLY way)
  eventSource = new EventSource(
    `/api/import/cp/progress?apiKey=${encodeURIComponent(apiKey)}`
  );

  eventSource.onmessage = (e) => {
    const msg = JSON.parse(e.data);
    progress.value.push(msg);

    if (msg.type === "done") {
      status.value = `Import completed in ${msg.totalSeconds}s`;
      running.value = false;
      eventSource.close();
    }

    if (msg.type === "error") {
      error.value = msg.message;
      running.value = false;
      eventSource.close();
    }
  };

  eventSource.onerror = () => {
    error.value = "Progress connection lost";
    running.value = false;
    eventSource.close();
  };
}

/* ============================================================
   Start import
============================================================ */
async function startImport() {
  if (!canStartImport.value) return;

  resetMessages();
  running.value = true;
  startProgressListener();

  try {
    const res = await fetch("/api/import/cp/start", {
      method: "POST",
      headers: apiHeaders(),
      body: JSON.stringify({
        cpKey: cpKey.value.trim(),
        cpBase: cpBase.value.trim(),
        reset: true,
      }),
    });

    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Failed to start import");

    status.value = "Import started…";
  } catch (err) {
    error.value = err.message || "Import start failed";
    running.value = false;
    eventSource?.close();
  }
}

/* ============================================================
   Cleanup
============================================================ */
onBeforeUnmount(() => {
  eventSource?.close();
});
</script>

<template>
  <section class="cp-wrap">
  <h1 class="tc">ComfortPages Import</h1>
    <div class="nz-nested">

      <div class="nz-grid-hh nz-nested-grid">
        <div class="nz-grid-item-hh"><div class="nz-nested-1">
          

          <div class="form-field">
            <label>CP API Key</label>
            <input v-model="cpKey" type="text" autocomplete="off" />
          </div>

          <div class="form-field">
            <label>CP Base URL</label>
            <input v-model="cpBase" type="text" />
          </div>

          <div class="actions">
            <button
              class="btn btn-danger"
              @click="startImport"
              :disabled="!canStartImport"
            >
              {{ running ? "Import running…" : "Start Import" }}
            </button>

            <button
              class="btn btn-primary"
              @click="testConnection"
              :disabled="testing || running"
            >
              {{ testing ? "Testing…" : "Test connection" }}
            </button>
          </div>


        </div></div>

        <div class="nz-grid-item-hh"><div class="nz-nested-2 nz-info">
      
          <div v-if="!status" class="success"><h3><span class="icon-heart-pulse"></span> Please realize, this is a complete import!</h3>All existing product data will be overwritten with the ComfortPages product data of your choice - loaded from the CP Base URL.</div>
          <p v-if="status" class="success">{{ status }}</p>
          <p v-if="error" class="error">{{ error }}</p>

          <!-- Progress -->
          <ul v-if="progress.length" class="progress">
            <li v-for="(p, i) in progress" :key="i">
              <template v-if="p.type === 'entity:start'">
                <span class="icon-cloud-upload"></span> {{ p.entity }} ({{ p.index }}/{{ p.total }})
              </template>
              <template v-else-if="p.type === 'entity:done'">
                <span class="icon-cloud-check"></span> {{ p.entity }} — {{ p.seconds }}s
              </template>
              <template v-else-if="p.type === 'truncate:start'">
                <span class="icon-cloud-gear"></span> Truncating tables…
              </template>
              <template v-else-if="p.type === 'done'">
                <div class="nz-cp-i-done"><span class="icon-cloud-check"></span> Import finished in {{ p.totalSeconds }}s</div>
              </template>
            </li>
          </ul> 

        </div></div>
      </div>
    </div>
  </section>
</template>

<style lang="less" scoped>
label {
  display: block;
  font-weight: 600;

}
input {
  width: 100%;
  padding: 0.6rem;
}
div.nz-nested{
  text-align: center;
}
ul.progress{
  width: 260px;
  display: inline-block;
  margin: 0 auto;
  list-style: none;
  li{
    text-align: left;
    span{
      font-size: 1.5rem;
      color: @accent;
      display: inline-block;
      margin: 3px 20px 3px 0px;
    }
  }

}
.nz-info h3{
  color: @danger;
  span{
    font-size: 3rem;
    display: block;
    margin: 0px auto 12px auto;
    color: @danger;
  }
}
.nz-cp-i-done{
  color: @accent;

}
.nz-grid-hh{
  max-width: 1200px;
  margin: 0 auto;

}
.nz-grid-item-hh{
  text-align: center;
  min-height: 700px;

}
.actions{
  width: 100%;
  text-align: center;
  button{
    margin: 10px 20px;
  }
}
.success {
  color: @accent;
  margin-top: 1rem;
}

.error {
  color: @danger;
  margin-top: 1rem;
}
</style>
